import { calendarDisplayDate, calendarDate } from '../../../../utils/date-time';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useFormHorarioClienteContext } from './Context';
import { LoadFailure, SlotGrid, SlotSkeleton, WeekStrip } from '../../booking/BookingParts';
import { AgendaClienteService } from '../Service';
import { FilaEsperaService } from '../../../fila-espera/Service';
import { useAppContext } from '../../../../contexts/AppContext';

const horarioService = new AgendaClienteService();
const filaService = new FilaEsperaService();

// Limite de dias consultados, um a um, na busca pelo proximo dia com vaga.
const SEARCH_LIMIT = 14;

export const SelectHorario = () => {
    const [loadingSlots, setLoadingSlots] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [searching, setSearching] = useState(false);
    const [noneAhead, setNoneAhead] = useState(false);
    const [horarios, setHorarios] = useState([] as number[]);
    const [waitlistLoading, setWaitlistLoading] = useState(false);
    const [waitlistRequested, setWaitlistRequested] = useState(false);
    const [retry, setRetry] = useState(0);
    const requestSeq = useRef(0);
    const autoSearched = useRef(false);

    const {
        date,
        data,
        handlePreviousWeek,
        handleNextWeek,
        setDate,
        estab,
        daysOpen,
        servico,
        servicosExtras,
        horario,
        setData,
        atendente,
        setHorario,
        loading: loadingForm,
    } = useFormHorarioClienteContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const totalDuration =
        Number(servico?.duration || 0) +
        servicosExtras.reduce((total: number, extra: any) => total + Number(extra?.duration || 0), 0);

    const today = moment(calendarDisplayDate(new Date(), estab?.timezone)).startOf('day');
    const limit = today.clone().add(daysOpen, 'days');
    const startOfWeek = moment(date).startOf('week');
    const endOfWeek = moment(date).endOf('week');

    useEffect(() => {
        // Ao voltar para a etapa no mesmo dia, mantem o horario ja escolhido.
        if (data instanceof Date && moment(data).isSame(date, 'day')) return;
        setData(date.toDate());
        setHorario(0);
    }, [date]);

    const fetchDay = (day: moment.Moment) =>
        horarioService.getHorarios(estab?._id, day.toDate(), servico?._id, atendente?.uid, totalDuration || undefined);

    const searchNextDay = async (seq: number) => {
        setSearching(true);
        try {
            const day = moment(date).add(1, 'day');
            for (let tries = 0; tries < SEARCH_LIMIT && !day.isAfter(limit, 'day'); tries += 1) {
                const { data: slots } = await fetchDay(day);
                if (seq !== requestSeq.current) return;
                if (Array.isArray(slots) && slots.length) {
                    setDate(day.clone());
                    return;
                }
                day.add(1, 'day');
            }
            setNoneAhead(true);
        } catch {
            if (seq === requestSeq.current) setNoneAhead(true);
        } finally {
            if (seq === requestSeq.current) setSearching(false);
        }
    };

    useEffect(() => {
        // `data` so vira Date depois do primeiro efeito; antes disso a consulta seria repetida.
        if (!estab?._id || !atendente?.uid || !servico?._id || !(data instanceof Date)) return;
        const seq = ++requestSeq.current;
        setHorarios([]);
        setLoadError(false);
        setNoneAhead(false);
        setLoadingSlots(true);
        fetchDay(date)
            .then(({ data: slots }) => {
                if (seq !== requestSeq.current) return;
                const list = Array.isArray(slots) ? slots : [];
                setHorarios(list);
                // Ao entrar na etapa, nao para em um dia fechado: abre no primeiro dia com vaga.
                if (!list.length && !autoSearched.current) {
                    autoSearched.current = true;
                    void searchNextDay(seq);
                }
            })
            .catch(() => {
                if (seq === requestSeq.current) setLoadError(true);
            })
            .finally(() => {
                if (seq === requestSeq.current) setLoadingSlots(false);
            });
    }, [estab, atendente, servico, servicosExtras, data, retry]);

    useEffect(() => {
        setWaitlistRequested(false);
    }, [date, servico?._id, atendente?.uid, servicosExtras]);

    const handleJoinWaitlist = async () => {
        if (!estab?._id || !servico?._id || !atendente?.uid) return;

        try {
            setWaitlistLoading(true);
            const start = moment(date).startOf('day').toDate();
            const end = moment(date).endOf('day').toDate();
            await filaService.createCliente({
                estabId: estab._id,
                servicoId: servico._id,
                servicosExtrasIds: servicosExtras?.map((extra: any) => extra?._id).filter(Boolean),
                attendantId: atendente?.uid,
                preferredStartAt: calendarDate(start),
                preferredEndAt: calendarDate(end),
                timeWindowStartMinutes: 0,
                timeWindowEndMinutes: 1439,
            });
            successSnackbar('Você entrou na fila de espera.');
            setWaitlistRequested(true);
        } catch (error) {
            console.error('Erro ao entrar na fila:', error);
            errorSnackbar('Não foi possível entrar na fila de espera.');
        } finally {
            setWaitlistLoading(false);
        }
    };

    const busy = loadingSlots || searching;
    const dayLabel = moment(date).format('dddd, DD/MM');

    return (
        <div id="agenda-step-horario-cliente" className="space-y-5">
            <WeekStrip
                timezone={estab?.timezone}
                date={date}
                onSelect={(day) => setDate(day)}
                onPrevious={handlePreviousWeek}
                onNext={handleNextWeek}
                canPrevious={startOfWeek.isAfter(today, 'day')}
                canNext={endOfWeek.isBefore(limit, 'day')}
                isDayDisabled={(day) => day.isBefore(today, 'day') || day.isAfter(limit, 'day')}
                disabled={loadingForm}
            />

            {busy ? (
                <SlotSkeleton label={searching ? 'Procurando o próximo dia com horário' : 'Carregando horários'} />
            ) : loadError ? (
                <LoadFailure message="Não foi possível carregar os horários." onRetry={() => setRetry((value) => value + 1)} />
            ) : !horarios.length ? (
                <div className="py-4 text-center">
                    <p className="font-medium text-base-content">Sem horários em {dayLabel}</p>
                    <p className="mx-auto mt-1 max-w-xs text-sm text-base-content/60">
                        {noneAhead ? 'Também não encontramos vagas nos próximos dias. Entre na fila de espera e avisaremos se abrir uma vaga.' : 'Veja outro dia ou entre na fila de espera deste dia.'}
                    </p>
                    <div className="mt-4 flex flex-col items-center gap-1">
                        {!noneAhead && (
                            <button type="button" className="btn btn-primary h-11 min-h-11 rounded-full px-6" onClick={() => void searchNextDay(requestSeq.current)}>
                                Ver próximo dia com vaga
                            </button>
                        )}
                        <button type="button" className="btn btn-ghost h-11 min-h-11 rounded-full px-6 text-primary" disabled={waitlistLoading || waitlistRequested} onClick={handleJoinWaitlist}>
                            {waitlistRequested ? 'Você está na fila deste dia' : 'Entrar na fila de espera'}
                        </button>
                    </div>
                </div>
            ) : (
                <SlotGrid slots={horarios} selected={horario} onSelect={setHorario} disabled={loadingForm} />
            )}
        </div>
    );
};
